import React, { useContext, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { PageContext } from '../page';
import {
  TripsMonitoredReportPath,
  MessageConfigurationPath,
  TestMessagingPath,
  ManageUserPath,
  FlightSearchPath,
  ManageTripGroupsPath,
  TripAlertBrandingPath,
} from '../../../appRoutes';
import { useNavigationPermissions } from './useNavigationPermissions';
import { getWeekAgoDate, openIssuesFlyout } from './navigationUtils';

const Navigation: React.FC = React.memo((): JSX.Element => {
  const { pushAlert } = useContext(PageContext);
  const permissions = useNavigationPermissions();

  const {
    canIssueReports,
    canManageIssues,
    canAccessTravelerMap,
    canViewWaivers,
    isAccountAdmin,
    isWaiverAdmin,
    canUseWaiverReports,
    multiTabView,
    showAdmin,
    isSuperAdmin,
  } = permissions;

  const hasIssueAccess = useMemo(
    () => canIssueReports || canManageIssues,
    [canIssueReports, canManageIssues]
  );

  const hasWaiverAccess = useMemo(
    () => canViewWaivers || isWaiverAdmin || canUseWaiverReports,
    [canViewWaivers, isWaiverAdmin, canUseWaiverReports]
  );

  const weekAgoDate = useMemo(() => getWeekAgoDate(), []);

  const handleOpenIssuesFlyout = useCallback(() => {
    openIssuesFlyout(pushAlert);
  }, [pushAlert]);

  return (
    <>
      {multiTabView && (
        <div className="row navigation">
          <nav className="row multi-nav">
            <div className="col-md-12">
              <div id="nav-menu">
                <ul role="menu">
                  {canAccessTravelerMap && (
                    <li>
                      <a href="/map" className="nav-link-with-badge">
                        TRAVELER MAP
                        <span className="nav-badge">
                          NEW
                        </span>
                      </a>
                    </li>
                  )}
                  {hasIssueAccess && (
                    <li>
                      TRIPS<i className="icon-arrow"></i>
                      <ul>
                        <a href={`/trips/search?weekAgo=${weekAgoDate}`}>
                          <li>TRIP SEARCH</li>
                        </a>
                        <Link to={FlightSearchPath}>
                          <li>FLIGHT SEARCH</li>
                        </Link>
                        {(isAccountAdmin || showAdmin) && (
                          <Link to={TripsMonitoredReportPath}>
                            <li>TRIPS MONITORED REPORT</li>
                          </Link>
                        )}
                      </ul>
                    </li>
                  )}
                  {hasIssueAccess && (
                    <li>
                      ISSUES<i className="icon-arrow"></i>
                      <ul>
                        {canManageIssues && (
                          <>
                            <a href="/issues/open">
                              <li>OPEN ISSUE LIST</li>
                            </a>
                            <a href="/issues/my">
                              <li>MY ISSUE LIST</li>
                            </a>
                            <a href="/issues/search">
                              <li>ISSUE SEARCH</li>
                            </a>
                          </>
                        )}
                        {canIssueReports && (
                          <a href="/issues/reporting">
                            <li>ISSUE REPORTS</li>
                          </a>
                        )}
                      </ul>
                    </li>
                  )}
                  {hasWaiverAccess && (
                    <li>
                      WAIVERS<i className="icon-arrow"></i>
                      <ul>
                        {canViewWaivers && (
                          <a href="/waivers">
                            <li>WAIVER LIST</li>
                          </a>
                        )}
                        {isWaiverAdmin && (
                          <a href="/waivers/workItems/list">
                            <li>WAIVER WORK ITEMS</li>
                          </a>
                        )}
                        {canUseWaiverReports && (
                          <a href="/waivers/reporting">
                            <li>WAIVER REPORTING</li>
                          </a>
                        )}
                      </ul>
                    </li>
                  )}

                  <a href="/agent/traveler/profile/search">
                    <li>TRAVELER SEARCH</li>
                  </a>

                  {isAccountAdmin && (
                    <li>
                      ACCOUNT SETTINGS<i className="icon-arrow"></i>
                      <ul>
                        <Link to={TestMessagingPath}>
                          <li>TEST MESSAGING</li>
                        </Link>
                        <Link to={TripAlertBrandingPath}>
                          <li>TRIP ALERT BRANDING</li>
                        </Link>
                        <Link to={MessageConfigurationPath}>
                          <li>EMAIL CONFIGURATION</li>
                        </Link>
                        <Link to={ManageUserPath}>
                          <li>MANAGE USERS</li>
                        </Link>
                        <Link to={ManageTripGroupsPath}>
                          <li>MANAGE TRIP GROUPS</li>
                        </Link>
                      </ul>
                    </li>
                  )}
                  {isSuperAdmin && (
                    <a href="/admin">
                      <li>ADMIN</li>
                    </a>
                  )}
                </ul>
              </div>
              {canManageIssues && (
                <div className="hidden-xs just-a-link">
                  <div
                    className="flyout"
                    onClick={handleOpenIssuesFlyout}
                    title="Issues Flyout"
                  >
                    <i className="sprites sprite-flyout-normal"></i>
                  </div>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </>
  );
});

Navigation.displayName = 'Navigation';

export default Navigation;